/**
 * @file WebSocketContext.jsx
 * @description Provides a central WebSocket connection using socket.io
 * @version 1.0.0
 * @copyright Copyright (c) 2024, NetCyberOps LLC
 */

import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {io} from 'socket.io-client';
import {KeycloakContext} from "./KeycloakContext.jsx";

const WebSocketContext = createContext(null);

const WebSocketProvider = ({children}) => {
    const {token} = useContext(KeycloakContext); // Get token from KeycloakContext
    const tokenRef = useRef(token);

    const [socket, setSocket] = useState(null);
    const [connected, setConnected] = useState(false);
    const [activeRooms, setActiveRooms] = useState([]);
    const activeRoomsRef = useRef(activeRooms);
    const eventListenersRef = useRef({});
    const wasDisconnected = useRef(false);

    // Update ref when token changes
    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    useEffect(() => {
        activeRoomsRef.current = activeRooms;
    }, [activeRooms]);


    useEffect(() => {
        const newSocket = io(import.meta.env.VITE_WEBSOCKET_URL || window.location.origin, {
            transports: ['websocket'],
        });

        setSocket(newSocket);

        // Connection events
        newSocket.on('connect', () => {
            // console.log('Connected to Socket.IO server');
            setConnected(true);
        });

        newSocket.on('disconnect', () => {
            // console.log('Disconnected from Socket.IO server');
            setConnected(false);
            wasDisconnected.current = true;
        });

        // Error handling
        newSocket.on('error', (error) => {
            console.log('Socket error:', error);
        });

        // The server reports per-room failures in the ack payload rather than on
        // 'error'. Without this a malformed room name fails silently and the UI
        // just stops receiving events.
        const logRoomErrors = (verb) => ({errors}) => {
            if (errors && Object.keys(errors).length > 0) {
                console.error(`Socket ${verb} errors:`, errors);
            }
        };
        newSocket.on('subscribed', logRoomErrors('subscribe'));
        newSocket.on('unsubscribed', logRoomErrors('unsubscribe'));

        // newSocket.on('connect_error', (error) => {
        //     console.log('Connection error:', error);
        // });

        // Cleanup on unmount
        return () => {
            if (newSocket) {
                // console.log('Cleaning up socket connection');
                newSocket.disconnect();
            }
        };
    }, []);

    const joinRoom = useCallback((roomNames) => {
        if (!socket || !connected) {
            console.warn('Cannot join room: socket not connected');
            return;
        }

        const roomsToJoin = Array.isArray(roomNames) ? roomNames : [roomNames];
        const newRoomsToJoin = roomsToJoin.filter(room => !activeRoomsRef.current.includes(room));

        if (newRoomsToJoin.length > 0) {
            setActiveRooms(prev => {
                return [...prev, ...newRoomsToJoin];
            });
            socket.emit('subscribe', {
                token: tokenRef.current,
                subscriptions: newRoomsToJoin
            });
        }
    }, [socket, connected, token]);

    const leaveRoom = useCallback((roomNames) => {
        if (!socket || !connected) {
            console.warn('Cannot leave room: socket not connected');
            return;
        }
        const roomsToLeave = Array.isArray(roomNames) ? roomNames : [roomNames];

        if (roomsToLeave.length > 0) {
            setActiveRooms(prev => prev.filter(room => !roomsToLeave.includes(room)));
            socket.emit('unsubscribe', {
                token: tokenRef.current,
                subscriptions: roomsToLeave
            });
        }
    }, [socket, connected]);

    const addEventListener = useCallback((eventName, callback) => {
        if (!socket) {
            console.warn('Cannot add event listener: socket not initialized');
            return () => {
            };
        }

        // Store listeners by event name
        if (!eventListenersRef.current[eventName]) {
            eventListenersRef.current[eventName] = [];

            // Add the actual socket listener only once per event type
            socket.on(eventName, (data) => {
                // Call all registered callbacks for this event
                eventListenersRef.current[eventName].forEach(cb => cb(data));
            });
        }

        // Add the callback to our registry
        if (!eventListenersRef.current[eventName].includes(callback)) {
            eventListenersRef.current[eventName].push(callback);
        }


        // Return a cleanup function
        return () => {
            if (eventListenersRef.current[eventName]) {
                eventListenersRef.current[eventName] = eventListenersRef.current[eventName].filter(
                    cb => cb !== callback
                );

                // If no more listeners for this event, remove the socket listener
                if (eventListenersRef.current[eventName].length === 0) {
                    socket.off(eventName);
                    delete eventListenersRef.current[eventName];
                }
            }
        };
    }, [socket]);

    const removeEventListener = useCallback((eventName, callback) => {
        if (!socket) {
            console.warn('Cannot remove event listener: socket not initialized');
            return;
        }

        if (eventListenersRef.current[eventName]) {
            // Remove the callback from our registry
            eventListenersRef.current[eventName] = eventListenersRef.current[eventName].filter(
                cb => cb !== callback
            );

            // If no more listeners for this event, remove the socket listener
            if (eventListenersRef.current[eventName].length === 0) {
                socket.off(eventName);
                delete eventListenersRef.current[eventName];
            }
        }
    }, [socket]);

    const emit = useCallback((eventName, data) => {
        if (!socket || !connected) {
            console.warn('Cannot emit event: socket not connected');
            return;
        }

        socket.emit(eventName, data);
    }, [socket, connected]);

    // Add effect to handle reconnection
    useEffect(() => {
        if (connected && socket && activeRooms.length > 0 && tokenRef.current && wasDisconnected.current) {
            // console.log('Re-joining rooms:', activeRooms);
            // Automatically rejoin rooms on reconnection
            socket.emit('subscribe', {
                token: tokenRef.current,
                subscriptions: activeRooms
            });

            // Reset the flag after handling reconnection
            wasDisconnected.current = false;
        }
    }, [connected, socket]);


    // Create the context value object
    const contextValue = {
        socket,
        connected,
        joinRoom,
        leaveRoom,
        addEventListener,
        removeEventListener,
        emit,
        activeRooms
    };

    return (
        <WebSocketContext.Provider value={contextValue}>
            {children}
        </WebSocketContext.Provider>
    );
};

const useWebSocket = () => {
    const context = useContext(WebSocketContext);

    if (!context) {
        throw new Error('useWebSocket must be used within a WebSocketProvider');
    }

    return context;
};

export {WebSocketProvider, useWebSocket};